import { FocusTable, ToolBarItemType, type ToolBarItem } from "@focus/focusUI";
import type {
  IColumnDefsConfig,
  ITableData,
  RowClassRules,
  ToolbarSettings,
} from "@focus/focusUI/lib/components/Tables/FocusTable/Entities";
import { useCallback, useRef, useState } from "react";
import type { ColumnFiltersState } from "../../services/helpers/pager";
import type { ServerColumnConfig } from "../../services/helpers/headers";
import { FiltersTag } from "./FiltersTag";
import { type TableActionType } from "./TableActionColumn";
import { TableSideBar } from "./TableSideBar";
import { useFocusocTable, type OnChangeFn } from "./useFocusocTable";
import { handleTableFilterChange } from "../../helpers/utils";

export type VisibilityState = Record<string, boolean>;
export type ColumnOrderState = string[];

interface TableProps {
  columnDefinitions: IColumnDefsConfig[];
  serverDisplayConfig: ServerColumnConfig;
  defaultVisibleValues: string[];
  adaptToContainerHeight?: boolean;
  rowData?: ITableData[];
  source?: string;
  base: string;
  actions?: TableActionType[];
  downloadData?: (param: { id: string }) => void;
  showOperatorColumn?: boolean;
  showSidebar?: boolean;
  leftMenuItems?: ToolBarItem<unknown>[];
  rightMenuItems?: ToolBarItem<unknown>[];
  rowClassRules?: RowClassRules;
  enableGrouping?: boolean;
  paginated?: boolean;
  height?: "auto" | number;
  toolbarSettings?: ToolbarSettings;
  controlledColumnFilter?: ColumnFiltersState;
  controlledColumnFilterChange?: OnChangeFn<ColumnFiltersState>;
  saveColumnLayout?: (serverConfig: ServerColumnConfig) => void;
  showGlobalFilter?: boolean;
  controlledRowSelection?: Record<string, boolean>;
  controlledRowSelectionChange?: OnChangeFn<Record<string, boolean>>;
  enableMultiSelection?: boolean;
}

export const Table: React.FC<TableProps> = ({
  columnDefinitions,
  serverDisplayConfig,
  defaultVisibleValues,
  rowData,
  source,
  downloadData,
  base,
  actions,
  showOperatorColumn = false,
  showSidebar = false,
  leftMenuItems,
  rightMenuItems = [],
  enableGrouping,
  paginated = true,
  height,
  toolbarSettings,
  rowClassRules,
  controlledColumnFilter,
  controlledColumnFilterChange,
  saveColumnLayout,
  showGlobalFilter = false,
  controlledRowSelection,
  controlledRowSelectionChange,
  enableMultiSelection,
}) => {
  const selectedFilterFnRef = useRef<Record<string, string>>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    controlledColumnFilter ?? [],
  );
  const handleChange: OnChangeFn<ColumnFiltersState> = useCallback((updater) => {
    const filtered = handleTableFilterChange(
      updater,
      columnFilters,
      selectedFilterFnRef.current,
    );
    controlledColumnFilterChange?.(filtered);
    setColumnFilters(filtered);
  }, [columnFilters, controlledColumnFilterChange]);

  const {
    columnDefs,
    columnVisibility,
    columnOrder,
    customGlobalFilterFn,
    toggleVisibility,
    handleColumnOrderChange,
    handleColumnResize,
  } = useFocusocTable({
    serverDisplayConfig,
    defaultVisibleValues,
    columnDefinitions,
    actions,
    base,
    source,
    downloadData,
    saveColumnLayout,
    showOperatorColumn,
  });

  const rightMenuElements: ToolBarItem<unknown>[] = [
    {
      type: ToolBarItemType.Custom,
      component: (
        <FiltersTag
          columnFilters={columnFilters}
          columnFiltersChange={handleChange}
          columnDefinitions={columnDefinitions}
        />
      ),
    },
    ...rightMenuItems,
  ];

  return (
    <div
      className={`flex h-full min-h-0 min-w-0 overflow-hidden ${
        paginated ? "" : "focusoc-table-no-pagination"
      }`}
    >
      <FocusTable
        className="min-w-0 flex-1"
        data={rowData}
        setData={(data) => console.log(data)}
        columnDefs={columnDefs}
        getRowId={(row) => row.id}
        paginated={paginated}
        paginationOptions={{
          initialPageSize: 30,
          initialPage: 0,
        }}
        disableEdition
        height={height}
        showGlobalFilter={showGlobalFilter}
        leftMenuItems={leftMenuItems}
        rightMenuItems={rightMenuElements}
        enableGrouping={enableGrouping}
        toolbarSettings={toolbarSettings}
        rowClassRules={rowClassRules}
        onColumnResize={handleColumnResize}
        controlledColumnFilters={columnFilters}
        controlledColumnFilterChange={handleChange}
        controlledColumnVisibility={columnVisibility}
        controlledColumnOrder={columnOrder}
        customGlobalFilterFn={customGlobalFilterFn}
        controlledRowSelection={controlledRowSelection}
        controlledRowSelectionChange={controlledRowSelectionChange}
        enableMultiSelection={enableMultiSelection}
        selectedFilterFnRef={selectedFilterFnRef}
      />
      {showSidebar && (
        <TableSideBar
          columnDefinitions={columnDefs}
          columnVisibility={columnVisibility}
          toggleVisibility={toggleVisibility}
          columnOrder={columnOrder}
          handleColumnOrderChange={handleColumnOrderChange}
        />
      )}
    </div>
  );
};
